/**
 * チバリヨ - 当期(会計年度) KPI 集計 共有モジュール (kpi-fiscal.js)
 *
 * config/system の fiscalYear / fiscalYearStart / fiscalYearEnd から「当期」を割り出し、
 * 期首〜当月の reports / goals を範囲クエリで一括取得して月別・累計に集計する。
 *
 * 使う側:
 *   - S5 (mobile-s5-kpi.js) : 当期進捗セクション (個人 / チーム)
 *   - P1 (p1-dashboard.js)  : 当期実績セクション (チーム)
 *
 * クエリ設計:
 *   - reports: date の単一フィールド範囲 (期首 〜 当月末)
 *   - goals  : yearMonth の単一フィールド範囲 (期首月 〜 当月)
 *   → どちらも単一フィールド自動インデックスで賄える (rules / indexes 変更不要)
 *
 * config の期間が古い場合 (期末を過ぎても config/system が未更新) は、
 * 期間を1年単位でスライドして「今日を含む期」に自動で読み替える
 * (fiscalYear 番号も同じだけ加減算)。admin が P6 で更新すればそのまま従う。
 *
 * 集計の原則 (P1 / S5 と共通):
 *   - チーム集計の対象 = 「その月の goals が設定されているメンバー」(月ごとに判定)
 *   - 個人集計は goals 未設定の月も実績は表示 (目標は「—」)
 */

import {
  collection,
  query,
  where,
  getDocs,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

// ─── 日付ユーティリティ ──────────────────────────────────────
function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatYearMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function parseDateStr(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addYears(d, n) {
  return new Date(d.getFullYear() + n, d.getMonth(), d.getDate());
}

/** 'YYYY-MM' → その月の末日 'YYYY-MM-DD' */
function endOfMonthStr(ym) {
  const [y, m] = ym.split('-').map(Number);
  return formatDate(new Date(y, m, 0));
}

/** 'YYYY-MM' → '2026年6月' */
export function formatYmJa(ym) {
  const [y, m] = ym.split('-');
  return `${y}年${Number(m)}月`;
}

// ─── 当期の割り出し ──────────────────────────────────────────
/**
 * @returns {{ fiscalYear: number|null, startDate: string, endDate: string,
 *             months: string[], currentYm: string }}
 *   months は 期首月〜min(当月, 期末月) の 'YYYY-MM' 配列 (未来月は含まない)
 */
export function resolveFiscalPeriod(config, now = new Date()) {
  let start = parseDateStr(config && config.fiscalYearStart);
  let end = parseDateStr(config && config.fiscalYearEnd);
  let fiscalYear = Number(config && config.fiscalYear) || null;

  // フォールバック: 設定がなければ暦年 (1月〜12月)
  if (!start || !end || start >= end) {
    start = new Date(now.getFullYear(), 0, 1);
    end = new Date(now.getFullYear(), 11, 31);
    fiscalYear = null;
  }

  // 今日を含む期になるまで1年単位でスライド (config 未更新対策)
  const todayStr = formatDate(now);
  let guard = 0;
  while (formatDate(end) < todayStr && guard++ < 100) {
    start = addYears(start, 1);
    end = addYears(end, 1);
    if (fiscalYear !== null) fiscalYear += 1;
  }
  while (formatDate(start) > todayStr && guard++ < 200) {
    start = addYears(start, -1);
    end = addYears(end, -1);
    if (fiscalYear !== null) fiscalYear -= 1;
  }

  const startDate = formatDate(start);
  const endDate = formatDate(end);
  const currentYm = formatYearMonth(now);
  const lastYm = endDate.slice(0, 7) < currentYm ? endDate.slice(0, 7) : currentYm;

  const months = [];
  for (
    let d = new Date(start.getFullYear(), start.getMonth(), 1);
    formatYearMonth(d) <= lastYm;
    d.setMonth(d.getMonth() + 1)
  ) {
    months.push(formatYearMonth(d));
  }

  return { fiscalYear, startDate, endDate, months, currentYm };
}

// ─── データ取得 ─────────────────────────────────────────────
/**
 * 期首〜当月末の reports と goals をまとめて取得。
 * @returns {{ reports: object[], goalsByMonth: Map<string, Map<string, {appointmentTarget:number, quoteTarget:number}>> }}
 */
export async function fetchFiscalKpiData(db, period) {
  const firstYm = period.months[0];
  const lastYm = period.months[period.months.length - 1];

  const [rSnap, gSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'reports'),
      where('date', '>=', period.startDate),
      where('date', '<=', endOfMonthStr(lastYm))
    )),
    getDocs(query(
      collection(db, 'goals'),
      where('yearMonth', '>=', firstYm),
      where('yearMonth', '<=', lastYm)
    )),
  ]);

  const reports = rSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const goalsByMonth = new Map();
  for (const d of gSnap.docs) {
    const g = d.data();
    if (!g.yearMonth || !g.memberId) continue;
    if (!goalsByMonth.has(g.yearMonth)) goalsByMonth.set(g.yearMonth, new Map());
    goalsByMonth.get(g.yearMonth).set(g.memberId, {
      appointmentTarget: g.appointmentTarget || 0,
      quoteTarget: g.quoteTarget || 0,
    });
  }

  return { reports, goalsByMonth };
}

// ─── 集計 ───────────────────────────────────────────────────
/**
 * 月別の実績/目標と累計を組み立てる。
 * @param {object} p
 * @param {object[]} p.reports        期内の日報全件
 * @param {Map} p.goalsByMonth        yearMonth → (memberId → goal)
 * @param {string[]} p.months         集計対象の 'YYYY-MM' 配列
 * @param {string|null} [p.memberId]  指定すると個人集計、null でチーム集計
 *                                    (チーム = その月の goals があるメンバーの合算)
 */
export function buildFiscalRows({ reports, goalsByMonth, months, memberId = null }) {
  const rows = months.map((ym) => {
    const monthGoals = goalsByMonth.get(ym) || new Map();

    let hasGoal;
    let appointmentTarget = 0;
    let quoteTarget = 0;
    let scoped;

    if (memberId) {
      const g = monthGoals.get(memberId);
      hasGoal = !!g;
      if (g) {
        appointmentTarget = g.appointmentTarget;
        quoteTarget = g.quoteTarget;
      }
      scoped = reports.filter((r) => r.memberId === memberId && r.date && r.date.startsWith(ym));
    } else {
      hasGoal = monthGoals.size > 0;
      for (const [, g] of monthGoals) {
        appointmentTarget += g.appointmentTarget;
        quoteTarget += g.quoteTarget;
      }
      scoped = reports.filter((r) => monthGoals.has(r.memberId) && r.date && r.date.startsWith(ym));
    }

    return {
      ym,
      hasGoal,
      appointmentTarget,
      quoteTarget,
      appointment: scoped.filter((r) => r.type === '訪問' && r.hasAppointment === true).length,
      quote: scoped.filter((r) => r.quote === true).length,
      visit: scoped.filter((r) => r.type === '訪問').length,
      reportCount: scoped.length,
    };
  });

  const totals = rows.reduce((t, r) => ({
    appointment: t.appointment + r.appointment,
    quote: t.quote + r.quote,
    visit: t.visit + r.visit,
    reportCount: t.reportCount + r.reportCount,
    appointmentTarget: t.appointmentTarget + r.appointmentTarget,
    quoteTarget: t.quoteTarget + r.quoteTarget,
  }), { appointment: 0, quote: 0, visit: 0, reportCount: 0, appointmentTarget: 0, quoteTarget: 0 });

  return { rows, totals };
}

// ─── 描画 (kf- prefix / kpi-fiscal.css) ─────────────────────
function fmtRate(actual, target) {
  if (!target) return '—';
  return `${Math.round(actual / target * 100)}%`;
}

function rateClass(actual, target) {
  if (!target) return 'is-none';
  const pct = actual / target * 100;
  if (pct >= 100) return 'is-complete';
  if (pct >= 75) return 'is-high';
  if (pct >= 50) return 'is-mid';
  return 'is-low';
}

function kfBar(actual, target) {
  if (!target) return '<div class="kf-bar"><div class="kf-bar-fill is-empty"></div></div>';
  const pct = Math.min(100, Math.round(actual / target * 100));
  return `<div class="kf-bar"><div class="kf-bar-fill ${rateClass(actual, target)}" style="width:${pct}%"></div></div>`;
}

function kfMetric(label, actual, target) {
  return `
    <div class="kf-metric">
      <div class="kf-metric-top">
        <span class="kf-metric-label">${label}</span>
        <span class="kf-metric-value">${actual} <span class="kf-metric-target">/ ${target || '—'}</span></span>
        <span class="kf-metric-rate">${fmtRate(actual, target)}</span>
      </div>
      ${kfBar(actual, target)}
    </div>
  `;
}

/** 月ラベル: 先頭行と年が変わる行だけ '2026年6月'、それ以外は '7月' */
function kfMonthLabel(ym, prevYm) {
  const [y, m] = ym.split('-');
  if (!prevYm || prevYm.slice(0, 4) !== y) return `${y}年${Number(m)}月`;
  return `${Number(m)}月`;
}

function kfNumCell(actual, target, hasGoal) {
  const t = hasGoal && target ? target : null;
  return `
    <td class="kf-num">
      <span class="kf-cell-val">${actual}<span class="kf-cell-target"> / ${t ?? '—'}</span></span>
      <span class="kf-rate ${rateClass(actual, t)}">${fmtRate(actual, t)}</span>
    </td>
  `;
}

/** 期内のいずれかの月に goals が設定されているメンバーIDの Set (メンバー別表示の対象) */
export function fiscalMemberIdSet(goalsByMonth) {
  const ids = new Set();
  for (const [, monthGoals] of goalsByMonth) {
    for (const id of monthGoals.keys()) ids.add(id);
  }
  return ids;
}

/**
 * 月別振り返りテーブル単体の HTML (累計は tfoot)。
 * メンバー別カードなど、累計サマリーブロックが不要な場面で使う。
 */
export function renderFiscalTableHtml({ rows, totals }, currentYm) {
  const body = rows.map((r, i) => {
    const isCurrent = r.ym === currentYm;
    return `
      <tr class="${isCurrent ? 'kf-row-current' : ''}">
        <th class="kf-month">
          ${kfMonthLabel(r.ym, i > 0 ? rows[i - 1].ym : null)}
          ${isCurrent ? '<span class="kf-now-badge">今月</span>' : ''}
        </th>
        ${kfNumCell(r.appointment, r.appointmentTarget, r.hasGoal)}
        ${kfNumCell(r.quote, r.quoteTarget, r.hasGoal)}
        <td class="kf-num kf-visit"><span class="kf-cell-val">${r.visit}</span></td>
      </tr>
    `;
  }).join('');

  return `
    <table class="kf-table">
      <thead>
        <tr>
          <th class="kf-month">月</th>
          <th class="kf-num">アポ</th>
          <th class="kf-num">見積</th>
          <th class="kf-num kf-visit">訪問</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
      <tfoot>
        <tr>
          <th class="kf-month">累計</th>
          ${kfNumCell(totals.appointment, totals.appointmentTarget, totals.appointmentTarget > 0)}
          ${kfNumCell(totals.quote, totals.quoteTarget, totals.quoteTarget > 0)}
          <td class="kf-num kf-visit"><span class="kf-cell-val">${totals.visit}</span></td>
        </tr>
      </tfoot>
    </table>
  `;
}

/**
 * 累計サマリー + 月別振り返りテーブルの HTML (カードの中身)。
 * 呼び出し側でカード/セクションの枠に入れて使う。
 */
export function renderFiscalContentHtml(fiscal, currentYm) {
  const { totals } = fiscal;
  return `
    <div class="kf-cumulative">
      ${kfMetric('アポ獲得 累計', totals.appointment, totals.appointmentTarget)}
      ${kfMetric('見積提出 累計', totals.quote, totals.quoteTarget)}
      <div class="kf-metric">
        <div class="kf-metric-top">
          <span class="kf-metric-label">訪問件数 累計</span>
          <span class="kf-metric-value">${totals.visit} <span class="kf-metric-unit">件</span></span>
          <span class="kf-metric-sub">日報 ${totals.reportCount}件</span>
        </div>
      </div>
    </div>
    ${renderFiscalTableHtml(fiscal, currentYm)}
  `;
}
